import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { companiesApi } from '@/api/companies';
import { accountsApi, Account } from '@/api/accounts';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert } from '@/components/ui/alert';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import {
  Pencil,
  Trash2,
  Check,
  X,
  Plus,
  Link,
  ToggleLeft,
  ToggleRight,
  Key,
  Save,
  Building2,
} from 'lucide-react';
import type { Company, UTAGECredentialsInput } from '@/types/company';

interface DataSource {
  id?: number;
  name: string;
  utage_url: string;
  is_active: boolean;
}

export default function MyCompany() {
  const { accessibleCompanies } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [company, setCompany] = useState<Company | null>(null);
  const [dataSources, setDataSources] = useState<Account[]>([]);

  // UTAGE認証情報
  const [utageCredentials, setUtageCredentials] = useState<UTAGECredentialsInput>({
    utage_login_url: '',
    utage_email: '',
    utage_password: '',
  });
  const [isUtageSubmitting, setIsUtageSubmitting] = useState(false);
  const [utageMessage, setUtageMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // データソース関連
  const [newDataSource, setNewDataSource] = useState<DataSource>({ name: '', utage_url: '', is_active: true });
  const [dataSourceError, setDataSourceError] = useState<string | null>(null);
  const [editingDataSourceId, setEditingDataSourceId] = useState<number | null>(null);
  const [editingDataSourceData, setEditingDataSourceData] = useState<DataSource>({ name: '', utage_url: '', is_active: true });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 初期データ取得
  useEffect(() => {
    const fetchData = async () => {
      if (accessibleCompanies.length === 0) {
        setIsLoading(false);
        return;
      }

      const companyId = accessibleCompanies[0].id;

      try {
        // 法人の詳細情報を取得
        const companyDetail = await companiesApi.getCompany(companyId);
        setCompany(companyDetail);
        setUtageCredentials({
          utage_login_url: companyDetail.utage_login_url || '',
          utage_email: companyDetail.utage_email || '',
          utage_password: '',
        });

        // データソースを取得
        const response = await accountsApi.getAccounts({ company_id: companyId });
        setDataSources(response.items);
      } catch (err) {
        console.error('データ取得エラー:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [accessibleCompanies]);

  // UTAGE認証情報更新
  const handleUtageCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company) return;

    setIsUtageSubmitting(true);
    setUtageMessage(null);

    try {
      const updateData: UTAGECredentialsInput = {};
      if (utageCredentials.utage_login_url) {
        updateData.utage_login_url = utageCredentials.utage_login_url;
      }
      if (utageCredentials.utage_email) {
        updateData.utage_email = utageCredentials.utage_email;
      }
      if (utageCredentials.utage_password) {
        updateData.utage_password = utageCredentials.utage_password;
      }

      const updatedCompany = await companiesApi.updateUTAGECredentials(company.id, updateData);

      // 更新後のデータで法人情報を更新
      setCompany({ ...company, ...updatedCompany });
      setUtageCredentials(prev => ({ ...prev, utage_password: '' }));
      setUtageMessage({ type: 'success', text: 'UTAGE認証情報を更新しました' });
    } catch (err: any) {
      setUtageMessage({
        type: 'error',
        text: err.response?.data?.detail || 'UTAGE認証情報の更新に失敗しました',
      });
    } finally {
      setIsUtageSubmitting(false);
    }
  };

  // データソース追加
  const handleAddDataSource = async () => {
    if (!company) return;
    if (!newDataSource.name.trim()) {
      setDataSourceError('タグ名を入力してください');
      return;
    }
    if (!newDataSource.utage_url.trim()) {
      setDataSourceError('URLを入力してください');
      return;
    }

    setIsSubmitting(true);
    setDataSourceError(null);

    try {
      const created = await accountsApi.createAccount({
        company_id: company.id,
        name: newDataSource.name.trim(),
        utage_url: newDataSource.utage_url.trim(),
        is_active: true,
      });
      setDataSources([...dataSources, created]);
      setNewDataSource({ name: '', utage_url: '', is_active: true });
    } catch (err: any) {
      setDataSourceError(err.response?.data?.detail || '取得先の追加に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  // データソース編集開始
  const handleStartEditDataSource = (ds: Account) => {
    setEditingDataSourceId(ds.id);
    setEditingDataSourceData({
      name: ds.name,
      utage_url: ds.utage_url,
      is_active: ds.is_active,
    });
    setDataSourceError(null);
  };

  // データソース編集キャンセル
  const handleCancelEditDataSource = () => {
    setEditingDataSourceId(null);
    setEditingDataSourceData({ name: '', utage_url: '', is_active: true });
  };

  // データソース保存
  const handleSaveDataSource = async () => {
    if (!editingDataSourceId) return;
    if (!editingDataSourceData.name.trim()) {
      setDataSourceError('タグ名を入力してください');
      return;
    }
    if (!editingDataSourceData.utage_url.trim()) {
      setDataSourceError('URLを入力してください');
      return;
    }

    setIsSubmitting(true);
    setDataSourceError(null);

    try {
      await accountsApi.updateAccount(editingDataSourceId, {
        name: editingDataSourceData.name.trim(),
        utage_url: editingDataSourceData.utage_url.trim(),
      });
      setDataSources(dataSources.map(ds =>
        ds.id === editingDataSourceId
          ? { ...ds, name: editingDataSourceData.name.trim(), utage_url: editingDataSourceData.utage_url.trim() }
          : ds
      ));
      setEditingDataSourceId(null);
    } catch (err: any) {
      setDataSourceError(err.response?.data?.detail || '取得先の更新に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  // データソース有効/無効切り替え
  const handleToggleDataSource = async (account: Account) => {
    try {
      await accountsApi.updateAccount(account.id, { is_active: !account.is_active });
      setDataSources(dataSources.map(ds =>
        ds.id === account.id ? { ...ds, is_active: !ds.is_active } : ds
      ));
    } catch (err) {
      console.error('データソース更新エラー:', err);
    }
  };

  // データソース削除
  const handleDeleteDataSource = async (id: number) => {
    if (!confirm('この取得先を削除してもよろしいですか？')) return;

    try {
      await accountsApi.deleteAccount(id);
      setDataSources(dataSources.filter(ds => ds.id !== id));
    } catch (err) {
      console.error('データソース削除エラー:', err);
    }
  };

  // データソースカード（モバイル用）
  const DataSourceCard = ({ ds }: { ds: Account }) => {
    const isEditing = editingDataSourceId === ds.id;

    if (isEditing) {
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <Input
            label="タグ名"
            value={editingDataSourceData.name}
            onChange={(e) => setEditingDataSourceData({ ...editingDataSourceData, name: e.target.value })}
            placeholder="オプトイン、個別案内など"
          />
          <Input
            label="URL"
            value={editingDataSourceData.utage_url}
            onChange={(e) => setEditingDataSourceData({ ...editingDataSourceData, utage_url: e.target.value })}
            placeholder="https://..."
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSaveDataSource} isLoading={isSubmitting}>
              <Check className="w-4 h-4 mr-1" />
              保存
            </Button>
            <Button variant="outline" size="sm" onClick={handleCancelEditDataSource}>
              <X className="w-4 h-4 mr-1" />
              キャンセル
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white border rounded-lg p-4 space-y-2">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-gray-900">{ds.name}</h4>
            <a
              href={ds.utage_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline break-all"
            >
              {ds.utage_url}
            </a>
          </div>
          <Badge variant={ds.is_active ? 'success' : 'default'} className="ml-2 shrink-0">
            {ds.is_active ? '有効' : '無効'}
          </Badge>
        </div>
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <Button variant="outline" size="sm" onClick={() => handleStartEditDataSource(ds)}>
            <Pencil className="w-4 h-4 mr-1" />
            編集
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleToggleDataSource(ds)}>
            {ds.is_active ? <ToggleRight className="w-4 h-4 mr-1" /> : <ToggleLeft className="w-4 h-4 mr-1" />}
            {ds.is_active ? '無効化' : '有効化'}
          </Button>
          <Button variant="danger" size="sm" onClick={() => handleDeleteDataSource(ds.id)}>
            <Trash2 className="w-4 h-4 mr-1" />
            削除
          </Button>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">法人情報が見つかりません</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">法人設定</h1>
        <p className="text-sm sm:text-base text-gray-500">
          {company.display_name} の設定を管理します
        </p>
      </div>

      {/* 法人情報 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            法人情報
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">法人名</p>
              <p className="font-medium">{company.display_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">システムID</p>
              <p className="font-medium text-gray-600">{company.name}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* UTAGE認証情報 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            UTAGE認証情報
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUtageCredentialsSubmit} className="space-y-4">
            {utageMessage && (
              <Alert
                variant={utageMessage.type === 'success' ? 'success' : 'error'}
                onClose={() => setUtageMessage(null)}
              >
                {utageMessage.text}
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="UTAGEログインURL"
                value={utageCredentials.utage_login_url || ''}
                onChange={(e) => setUtageCredentials({ ...utageCredentials, utage_login_url: e.target.value })}
                placeholder="https://member.jyorei.net/operator/XXXXX/login"
                helperText="法人のUTAGEログインページURL"
              />
              <Input
                label="UTAGEメールアドレス"
                type="email"
                value={utageCredentials.utage_email || ''}
                onChange={(e) => setUtageCredentials({ ...utageCredentials, utage_email: e.target.value })}
                placeholder="utage@example.com"
              />
            </div>

            <div className="max-w-md">
              <Input
                label="UTAGEパスワード"
                type="password"
                value={utageCredentials.utage_password || ''}
                onChange={(e) => setUtageCredentials({ ...utageCredentials, utage_password: e.target.value })}
                placeholder="新しいパスワードを入力（変更する場合のみ）"
                helperText="変更しない場合は空欄のままにしてください"
              />
            </div>

            <Button type="submit" isLoading={isUtageSubmitting}>
              <Save className="w-4 h-4 mr-1" />
              認証情報を保存
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* 取得先ページ管理 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link className="w-5 h-5" />
            取得先ページ管理
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-gray-600">
            UTAGEからデータを自動取得するページを登録します。名前で分類し、それぞれのURLからデータを収集します。
          </p>

          {/* 取得先ページ一覧 */}
          {dataSources.length > 0 ? (
            <>
              {/* デスクトップ: テーブル表示 */}
              <div className="hidden md:block border rounded-lg overflow-hidden overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700 w-40">タグ名</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">URL</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700 w-20">状態</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700 w-36">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {dataSources.map((ds) => (
                      <tr key={ds.id} className={editingDataSourceId === ds.id ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                        {editingDataSourceId === ds.id ? (
                          <>
                            <td className="px-4 py-2">
                              <Input
                                value={editingDataSourceData.name}
                                onChange={(e) => setEditingDataSourceData({ ...editingDataSourceData, name: e.target.value })}
                                placeholder="タグ名"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Input
                                value={editingDataSourceData.utage_url}
                                onChange={(e) => setEditingDataSourceData({ ...editingDataSourceData, utage_url: e.target.value })}
                                placeholder="URL"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Badge variant={ds.is_active ? 'success' : 'default'}>
                                {ds.is_active ? '有効' : '無効'}
                              </Badge>
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex gap-1">
                                <Button size="sm" onClick={handleSaveDataSource} isLoading={isSubmitting} title="保存">
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={handleCancelEditDataSource} title="キャンセル">
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-3 font-medium whitespace-nowrap">{ds.name}</td>
                            <td className="px-4 py-3">
                              <a
                                href={ds.utage_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline break-all"
                                title={ds.utage_url}
                              >
                                {ds.utage_url}
                              </a>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={ds.is_active ? 'success' : 'default'}>
                                {ds.is_active ? '有効' : '無効'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1">
                                <Button variant="outline" size="sm" onClick={() => handleStartEditDataSource(ds)} title="編集">
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => handleToggleDataSource(ds)} title={ds.is_active ? '無効化' : '有効化'}>
                                  {ds.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                                </Button>
                                <Button variant="danger" size="sm" onClick={() => handleDeleteDataSource(ds.id)} title="削除">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* モバイル: カード表示 */}
              <div className="md:hidden space-y-3">
                {dataSources.map((ds) => (
                  <DataSourceCard key={ds.id} ds={ds} />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500 border rounded-lg bg-gray-50">
              取得先ページが登録されていません
            </div>
          )}

          {/* 新規追加フォーム */}
          <div className="border-t pt-6">
            <h4 className="font-medium mb-4 flex items-center gap-2">
              <Plus className="w-4 h-4" />
              新しい取得先を追加
            </h4>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="md:w-48 shrink-0">
                <Input
                  label="タグ名"
                  value={newDataSource.name}
                  onChange={(e) => setNewDataSource({ ...newDataSource, name: e.target.value })}
                  placeholder="オプトイン、個別案内など"
                />
              </div>
              <div className="flex-1">
                <Input
                  label="UTAGE URL"
                  value={newDataSource.utage_url}
                  onChange={(e) => setNewDataSource({ ...newDataSource, utage_url: e.target.value })}
                  placeholder="https://member.jyorei.net/account/XXXXX/label"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={handleAddDataSource} isLoading={isSubmitting} className="w-full md:w-auto">
                  <Plus className="w-4 h-4 mr-1" />
                  追加
                </Button>
              </div>
            </div>

            {dataSourceError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{dataSourceError}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
